import React from "react";
import { useApolloClient } from "@apollo/client";
import { Button, ButtonProps, exportFile, Modal, toCSV, useUpdatingRef } from "@hex-insights/core";
import { DownloadProgressModal } from "../../../../Components/Export";
import {
	ClassYearNameFragment,
	mergeFilterInputs,
	OrderDirection,
	StudentFilterInput,
	StudentOrder,
	StudentOrderField,
	StudentPaginationDocument,
	StudentsSchoolAttendanceListDocument,
	StudentsSchoolAttendanceListQuery,
} from "../../../../Utilities";
import {
	AttendanceStatusFormat,
	ClassYearFormat,
	HomeRoomSectionFormat,
	SchoolAttendanceRecordFormat,
	StudentFormat,
	UserFormat,
} from "../../../Format";
import { ClassYearUtilities, StudentAcademicTermEnrollmentUtilities } from "../../../Utilities";

const studentOrder: StudentOrder = {
	field: StudentOrderField.GraduationYear,
	direction: OrderDirection.Desc,
};

const dataQuery = StudentsSchoolAttendanceListDocument;

const paginationQuery = StudentPaginationDocument;

const resultKey = "studentConnection";

const resultsPerPage = 50;

const fileNamePrefix = "school-attendance";

const columns: string[] = [
	"name",
	"classYear",
	"homeRoomSection",
	"attendanceStatus",
	"attendanceCode",
	"attendanceTakenAt",
	"attendanceTakenBy",
];

function formatRow(
	node: StudentsSchoolAttendanceListQuery["studentConnection"]["edges"][0]["node"],
	classYears: Record<number, ClassYearNameFragment>,
) {
	const classYear = node.graduationYear ? classYears[node.graduationYear] : null;
	const homeRoomSection = node.studentHomeRoomSectionEnrollments[0]?.homeRoomSection;
	const attendanceRecord = node.schoolAttendanceRecords[0];
	const attendanceStatus = attendanceRecord?.attendanceStatus;

	return {
		name: StudentFormat.name(node),
		classYear: classYear ? ClassYearFormat.name(classYear) : "",
		homeRoomSection: homeRoomSection ? HomeRoomSectionFormat.name(homeRoomSection) : "",
		attendanceStatus: attendanceStatus ? AttendanceStatusFormat.name(attendanceStatus) : "Not Taken",
		attendanceCode: attendanceStatus ? AttendanceStatusFormat.Fields.code(attendanceStatus.code) : "",
		attendanceTakenAt: attendanceRecord
			? SchoolAttendanceRecordFormat.Fields.createdAt(attendanceRecord.createdAt)
			: "",
		attendanceTakenBy:
			attendanceRecord && attendanceRecord.author
				? UserFormat.name({ ...attendanceRecord.author, isCurrent: false })
				: "",
	};
}

export type StudentsSchoolAttendanceListExportButtonProps = Omit<ButtonProps, "onClick"> & {
	date: string;
	studentFilters?: StudentFilterInput;
};

export function StudentsSchoolAttendanceListExportButton({
	date,
	studentFilters,
	disabled,
	...props
}: StudentsSchoolAttendanceListExportButtonProps) {
	const baseStudentFilters = React.useMemo<StudentFilterInput>(
		() => ({
			studentAcademicTermEnrollments: [StudentAcademicTermEnrollmentUtilities.getEnrolledOnDateFilter(date)],
		}),
		[date],
	);

	// TODO logic copied from Components/Export, refactor to unite

	const { loading: loadingClassYears, classYears } = ClassYearUtilities.useClassYearsMap();

	const client = useApolloClient();

	const { isModalOpen, openModal, closeModal: baseCloseModal } = Modal.useToggle();
	const isModalOpenRef = useUpdatingRef(isModalOpen);
	const [totalPages, setTotalPages] = React.useState(0);
	const [currentPage, setCurrentPage] = React.useState(0);

	const closeModal = React.useCallback(() => {
		baseCloseModal();
		window.setTimeout(() => {
			setCurrentPage(0);
		}, 250);
	}, [baseCloseModal]);

	const onClick = React.useCallback(async () => {
		openModal();

		const fullStudentFilters = studentFilters
			? mergeFilterInputs(studentFilters, baseStudentFilters)
			: baseStudentFilters;

		const { data: paginationData } = await client.query({
			query: paginationQuery,
			variables: { filters: fullStudentFilters, order: studentOrder },
		});
		const connection = paginationData[resultKey];
		const totalPages = Math.ceil(connection.totalCount / resultsPerPage);
		setTotalPages(totalPages);
		const rows = [];
		for (let i = 0; i < totalPages; i++) {
			if (!isModalOpenRef.current) {
				setTotalPages(0);
				setCurrentPage(0);
				return;
			}
			const cursor = i > 0 ? connection.edges[i * resultsPerPage - 1].cursor : null;
			const { data } = await client.query({
				query: dataQuery,
				variables: {
					date,
					filters: fullStudentFilters,
					order: studentOrder,
					pagination: { first: resultsPerPage, after: cursor },
				},
				fetchPolicy: "network-only",
			});
			rows.push(...data[resultKey].edges.map((e: any) => formatRow(e.node, classYears)));
			setCurrentPage(i + 1);
		}

		const file = toCSV(`${fileNamePrefix}-${new Date().getTime()}`, rows, columns);
		exportFile(file);

		window.setTimeout(() => {
			closeModal();
		}, 750);
	}, [openModal, closeModal, client, date, studentFilters, baseStudentFilters, classYears, isModalOpenRef]);

	const isDownloading = totalPages === 0 || currentPage < totalPages;

	return (
		<React.Fragment>
			<Button onClick={onClick} disabled={disabled || loadingClassYears} {...props} />

			<Modal.If condition={isModalOpen}>
				<DownloadProgressModal
					value={currentPage}
					total={totalPages}
					isDownloading={isDownloading}
					onClose={closeModal}
				/>
			</Modal.If>
		</React.Fragment>
	);
}
